
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PortfolioNavigation,
  PortfolioDesktopSelector,
  PortfolioSettingsWrapper
} from './components';
import MobileFooterButtons from '@/components/portfolio/settings/MobileFooterButtons';
import UnsavedChangesDialog from '@/components/portfolio/settings/UnsavedChangesDialog';
import { PortfolioSettingsStateType } from '@/components/portfolio/settings/types/portfolioSettingsTypes';
import { 
  handleTabChange, 
  handleBack, 
  handlePortfolioChange, 
  handleAddPaymentMethod,
  handleAddPromoCode,
  handleMakePrimary
} from '@/components/portfolio/settings/utils/portfolioNavigationUtils';
import { prepareBillingData } from '@/components/portfolio/settings/billing/billingDataUtils';

interface PortfolioSettingsContentProps {
  isMobile: boolean;
  portfolioId: string;
  portfolioOptions: Array<{id: string; title: string; role: string | null}>;
  saveInProgressRef: React.MutableRefObject<boolean>;
  portfolioSettingsState: PortfolioSettingsStateType;
  handleSaveWithThrottle: (e?: React.MouseEvent<HTMLButtonElement>) => void;
}

const PortfolioSettingsContent = ({
  isMobile,
  portfolioId,
  portfolioOptions,
  saveInProgressRef,
  portfolioSettingsState,
  handleSaveWithThrottle
}: PortfolioSettingsContentProps) => {
  const navigate = useNavigate();

  const {
    name,
    formEdited,
    activeTab,
    selectedPortfolio,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    portfolioData,
    paymentMethods,
    discountCodes,
    totalCharge,
    showUnsavedChangesDialog,
    setShowUnsavedChangesDialog,
    pendingAction,
    setPendingAction,
    handleInputChange,
    handleCancel,
    formatCurrency,
    setActiveTab,
    setSelectedPortfolio,
    toast
  } = portfolioSettingsState;

  // Handler for billing manager changes
  const handleChangeBillingManager = (userId: string) => {
    console.log('Changing billing manager to user ID:', userId);
    toast({
      title: "Billing manager updated",
      description: "The new billing manager has been assigned."
    });
  };

  // Prepare billing data for the tab content - update order of parameters
  const billingData = prepareBillingData(
    portfolioData,
    totalCharge,
    paymentMethods,
    discountCodes,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    () => handleAddPaymentMethod(toast),
    () => handleAddPromoCode(toast),
    (methodId) => handleMakePrimary(methodId, toast),
    formatCurrency,
    handleChangeBillingManager
  );

  const handleTabChangeLocal = (value: string) => {
    handleTabChange(
      value, 
      activeTab, 
      formEdited, 
      setPendingAction, 
      setShowUnsavedChangesDialog,
      setActiveTab
    );
  };

  const handleBackLocal = () => {
    handleBack(
      activeTab,
      formEdited,
      navigate,
      setPendingAction,
      setShowUnsavedChangesDialog
    );
  };

  const handlePortfolioChangeLocal = (value: string) => {
    console.log('PortfolioSettings - Portfolio Change Requested:', value);
    if (value === selectedPortfolio) {
      console.log('Selected the same portfolio - no changes needed');
      return;
    }
    
    handlePortfolioChange(
      value,
      activeTab,
      formEdited,
      navigate,
      setPendingAction,
      setShowUnsavedChangesDialog,
      setSelectedPortfolio
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col w-full overflow-x-hidden">
      <PortfolioNavigation
        portfolioId={portfolioId}
        selectedPortfolio={selectedPortfolio || portfolioId || ''}
        portfolioOptions={portfolioOptions}
        handlePortfolioChange={handlePortfolioChangeLocal}
        handleBack={handleBackLocal}
      />

      <main className="flex-1 max-w-screen-xl mx-auto px-4 md:px-8 py-6 w-full overflow-x-hidden">
        {/* New Portfolio Dropdown Row - Only shown on desktop */}
        {!isMobile && (
          <PortfolioDesktopSelector
            selectedPortfolio={selectedPortfolio || portfolioId || ''}
            portfolioOptions={portfolioOptions}
            onPortfolioChange={handlePortfolioChangeLocal}
          />
        )}

        <PortfolioSettingsWrapper
          activeTab={activeTab}
          formEdited={formEdited}
          isMobile={isMobile}
          name={name}
          handleInputChange={handleInputChange}
          handleCancel={handleCancel}
          handleSave={handleSaveWithThrottle}
          handleTabChange={handleTabChangeLocal}
          billingData={billingData}
        />
      </main>
      
      <MobileFooterButtons 
        formEdited={formEdited}
        activeTab={activeTab}
        handleCancel={handleCancel}
        handleSave={handleSaveWithThrottle}
      />
      
      <UnsavedChangesDialog 
        open={showUnsavedChangesDialog}
        onOpenChange={setShowUnsavedChangesDialog}
        onSave={handleSaveWithThrottle}
        onDiscard={handleCancel}
      />
    </div>
  );
};

export default PortfolioSettingsContent;
